import { useMemo } from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import classes from "./styles.module.scss";

import { StatusChipBase } from "@components/atoms/statusChipBase";
import { appointmentStatusData } from "@components/molecules/appointmentStatus/types";
import type {
  IAppointmentStatus,
  IAppointmentStatusProps,
} from "@components/molecules/appointmentStatus";

/**
 * Component that displays appointment status chips.
 *
 * @param {IAppointmentStatusProps} props - The props for the AppointmentStatus component.
 * @param {string} props.status - The status of the appointment.
 * @returns AppointmentStatus The rendered AppointmentStatus component.
 */
export function AppointmentStatus(props: IAppointmentStatusProps) {
  const { status, isCard, noShowCause, noShowReason } = props;
  const { t } = useTranslation("form");
  const { t: tCommon } = useTranslation("common");

  // Use useMemo to memoize the rendered appointment status chips.
  const renderAppointmentStatus = useMemo(() => {
    const getStatusLabel = () => {
      if (status === "no_show") {
        return noShowCause
          ? `${appointmentStatusData(tCommon)[status]?.label} (${t(
              noShowCause,
            )})`
          : `${appointmentStatusData(tCommon)[status]?.label} (${t(
              "pending",
            )})`;
      }
      if (status === "completed" && noShowCause)
        return `${appointmentStatusData(tCommon)[status]?.label} (${t(
          noShowCause,
        )})`;
      if (noShowReason)
        return `${appointmentStatusData(tCommon)[status]
          ?.label} (${noShowReason})`;
      return appointmentStatusData(tCommon)[status]?.label;
    };

    if (isCard)
      return (
        <StatusChipBase
          key={status}
          label={getStatusLabel()}
          color={appointmentStatusData(tCommon)[status]?.color}
        />
      );
    return (
      <>
        {Object?.keys(appointmentStatusData(tCommon))?.map((key) => {
          const statusData =
            appointmentStatusData(tCommon)[key as IAppointmentStatus];

          return (
            <StatusChipBase
              key={key}
              label={statusData?.label}
              color={statusData?.color}
              disabled={status !== key}
            />
          );
        })}
      </>
    );
  }, [isCard, noShowCause, noShowReason, status, t, tCommon]);

  return (
    // Render the appointment status chips in a row.
    <div className={clsx(classes.appointmentStatusRow)}>
      {renderAppointmentStatus}
    </div>
  );
}
